import { LanguageName, LanguageFluency, DocumentStatus, PolicyHolderRelationship, InsurancePlanType, Role, UserStatus, StateCode, DefaultLocation } from '@caresend/types';
import { initElementTimestamp } from './general.js';
import { generateID, generateUserID } from './id.js';

const initialLanguage = {
  language: LanguageName.ENGLISH,
  fluency: LanguageFluency.NATIVE
};
const initLicense = () => ({
  number: '',
  stateCode: undefined,
  id: generateID(),
  expiryDate: undefined,
  status: DocumentStatus.PENDING
}); // TODO: Update to LocationObject

const initialAddress = () => ({
  formattedAddress: '',
  lat: 0,
  lng: 0,
  addressComponents: []
});
const initialPhone = () => ({
  callingCode: '+1',
  countryCode: 'US',
  number: ''
});
const initialPolicyHolderInfo = () => ({
  firstName: '',
  lastName: '',
  dateOfBirth: undefined,
  phone: initialPhone(),
  address: initialAddress(),
  relationship: PolicyHolderRelationship.OTHER
});
const initialInsuranceInfo = () => ({
  carrier: '',
  carrierID: '',
  number: '',
  planType: InsurancePlanType.HMO,
  primary: false
});

const initialMailingAddress = () => ({
  address1: '',
  city: '',
  state: StateCode.CA,
  postalCode: '',
  country: DefaultLocation.USA
});

const initialInsuranceProvider = (name = '') => ({
  address: initialMailingAddress(),
  id: generateID(),
  name,
  phone: initialPhone()
});
/**
 * @deprecated Initializes with empty strings and objects. If we need to init
 * prior to having required fields, init as `Draft<EncryptedUser>` then validate
 * to `EncryptedUser` before saving to db.
 */

const initPatient = () => ({
  creationTimestamp: Date.now(),
  id: generateUserID(),
  info: {
    dateOfBirth: undefined,
    firstName: '',
    lastName: '',
    email: '',
    middleNames: '',
    address: initialAddress(),
    languages: [initialLanguage]
  },
  isNotificationRecipient: true,
  passwordCreated: false,
  role: Role.PATIENT,
  status: UserStatus.INCOMPLETE
});
const initNewPatient = (firstName, lastName, email) => ({
  creationTimestamp: Date.now(),
  id: generateUserID(),
  info: {
    firstName,
    lastName,
    email,
    languages: [initialLanguage]
  },
  isNotificationRecipient: true,
  passwordCreated: false,
  role: Role.PATIENT,
  status: UserStatus.INCOMPLETE
});
/**
 * Takes an existing patient object, and adds empty structures for missing values.
 * Prevents runtime errors in `PatientEditor.vue`
 * TODO: Remove and add proper null checking or initialization as needed to
 * `PatientEditor.vue`
 */

const initExistingPatientForEditing = user => {
  var _a, _b, _c, _d;

  return Object.assign(Object.assign({}, user), {
    // backwards compatibility
    isNotificationRecipient: (_a = user.isNotificationRecipient) !== null && _a !== void 0 ? _a : true,
    info: Object.assign(Object.assign({}, user.info), {
      address: (_b = user.info.address) !== null && _b !== void 0 ? _b : initialAddress(),
      languages: (_c = user.info.languages) !== null && _c !== void 0 ? _c : [initialLanguage],
      phone: (_d = user.info.phone) !== null && _d !== void 0 ? _d : initialPhone()
    })
  });
};
const initialEducation = {
  major: '',
  graduationYear: '',
  university: '',
  id: '1'
};
const initialLocation = {
  current: {
    lat: 0,
    lng: 0
  },
  custom: {
    lat: 0,
    lng: 0
  },
  fromCustomLocation: false,
  travelRadius: 40
};
const initialExperience = {
  employer: '',
  role: '',
  startYear: '',
  endYear: '',
  stillEmployed: false,
  id: '1'
};
const initialCertification = {
  name: '',
  id: '1',
  picture: '',
  expiryDate: undefined,
  stateCode: 'N/A',
  status: DocumentStatus.PENDING
};
const initialMalpracticeInsurance = {
  policyNumber: '',
  carrier: '',
  expiryDate: 0,
  picture: '',
  status: DocumentStatus.PENDING
};
const initIdVerification = () => ({
  front: '',
  back: '',
  status: DocumentStatus.PENDING
});

const initPrescriber = officeID => Object.assign(Object.assign({
  creationTimestamp: Date.now(),
  id: '',
  idVerification: initIdVerification(),
  info: {
    businessName: '',
    firstName: '',
    lastName: '',
    middleNames: '',
    phone: initialPhone(),
    email: ''
  }
}, officeID && {
  offices: [initElementTimestamp(officeID)]
}), {
  professionalInfo: {
    licences: [initLicense()],
    title: ''
  },
  passwordCreated: false,
  role: Role.PRESCRIBER,
  status: UserStatus.INCOMPLETE
});

const initAssistant = officeID => Object.assign(Object.assign({
  creationTimestamp: Date.now(),
  id: '',
  info: {
    businessName: '',
    firstName: '',
    lastName: '',
    phone: initialPhone(),
    email: ''
  }
}, officeID && {
  offices: [initElementTimestamp(officeID)]
}), {
  passwordCreated: false,
  managedPrescribers: {},
  role: Role.ASSISTANT,
  status: UserStatus.INCOMPLETE
});

const initNurse = () => ({
  creationTimestamp: Date.now(),
  id: '',
  status: UserStatus.INCOMPLETE,
  info: {
    dateOfBirth: undefined,
    firstName: '',
    lastName: '',
    email: '',
    middleNames: '',
    address: initialAddress(),
    phone: initialPhone(),
    languages: [initialLanguage],
    picture: '',
    gender: undefined,
    biography: ''
  },
  professionalInfo: {
    malpracticeInsurance: Object.assign({}, initialMalpracticeInsurance),
    licences: [initLicense()],
    title: ''
  },
  passwordCreated: false,
  location: initialLocation,
  educations: [Object.assign({}, initialEducation)],
  experiences: [Object.assign({}, initialExperience)],
  certifications: [Object.assign({}, initialCertification)],
  tasks: {},
  idVerification: initIdVerification(),
  role: Role.NURSE
});

const initStaffUser = (role, officeID) => {
  switch (role) {
    case Role.PRESCRIBER:
      return initPrescriber(officeID);

    case Role.NURSE:
      return initNurse();

    case Role.ASSISTANT:
    default:
      return initAssistant(officeID);
  }
};

export { initExistingPatientForEditing, initIdVerification, initLicense, initNewPatient, initPatient, initStaffUser, initialAddress, initialInsuranceInfo, initialInsuranceProvider, initialPhone, initialPolicyHolderInfo };
